import { DEFAULT_FLAME_THEME, type EventTheme } from "@tih/shared";
import {
  accentToInkShades,
  applyInkShades,
  edgeToInkShades,
  resetInkShades,
  type InkShades,
} from "./theme-ink";

export const DEFAULT_ACCENT_CSS = {
  accent: DEFAULT_FLAME_THEME.primary,
  accent2: DEFAULT_FLAME_THEME.highlight,
  accentDeep: DEFAULT_FLAME_THEME.deep,
} as const;

/** Apply three accent hex values to document root CSS variables. */
export function applyAccentHex(c1: string, c2: string, c3: string): void {
  const root = document.documentElement.style;
  root.setProperty("--accent", c1);
  root.setProperty("--accent-2", c2);
  root.setProperty("--accent-deep", c3);
}

/** Reset accent and background CSS variables to bundled defaults. */
export function resetAccentToDefault(): void {
  applyAccentHex(
    DEFAULT_ACCENT_CSS.accent,
    DEFAULT_ACCENT_CSS.accent2,
    DEFAULT_ACCENT_CSS.accentDeep,
  );
  resetInkShades();
}

function resolveThemeColors(theme: EventTheme): { c1: string; c2: string; c3: string } {
  return {
    c1: theme.usePrimary ? theme.primary : DEFAULT_ACCENT_CSS.accent,
    c2: theme.useHighlight ? theme.highlight : DEFAULT_ACCENT_CSS.accent2,
    c3: theme.useDeep ? theme.deep : DEFAULT_ACCENT_CSS.accentDeep,
  };
}

function resolveInkShades(theme: EventTheme, c1: string, c3: string): InkShades {
  if (theme.useBackground) {
    return edgeToInkShades(theme.background);
  }
  return accentToInkShades(c1, c3);
}

/**
 * Apply featured-event theme accents and dark tinted backgrounds on the public site.
 * Resets to defaults when theme is absent or not enabled for live display.
 */
export function applyEventTheme(theme: EventTheme | null | undefined): void {
  if (!theme?.applyWhenLive) {
    resetAccentToDefault();
    return;
  }
  const { c1, c2, c3 } = resolveThemeColors(theme);
  applyAccentHex(c1, c2, c3);
  applyInkShades(resolveInkShades(theme, c1, c3));
}

/** Resolve accent + ink shades for admin previews without touching :root. */
export function resolveThemePreviewVars(theme: EventTheme): Record<string, string> {
  const c1 = theme.usePrimary ? theme.primary : DEFAULT_ACCENT_CSS.accent;
  const c2 = theme.useHighlight ? theme.highlight : DEFAULT_ACCENT_CSS.accent2;
  const c3 = theme.useDeep ? theme.deep : DEFAULT_ACCENT_CSS.accentDeep;
  const ink = resolveInkShades(theme, c1, c3);
  return {
    "--accent": c1,
    "--accent-2": c2,
    "--accent-deep": c3,
    "--ink-0": ink.ink0,
    "--ink-1": ink.ink1,
    "--ink-2": ink.ink2,
    "--ink-3": ink.ink3,
    "--ink-4": ink.ink4,
  };
}
